import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, tap } from 'rxjs';
import { take } from 'rxjs/operators';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import {
  ApiMetricData,
  AppointmentMetricService,
} from './appointment-metric.service';

interface PendingRequest {
  startTime: number;
  request: HttpRequest<unknown>;
}

@Injectable()
export class AppointmentMetricInterceptor implements HttpInterceptor {
  private pendingRequests = new Map<string, PendingRequest>();

  constructor(
    private metricService: AppointmentMetricService,
    private environmentService: EnvironmentService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.shouldTrackRequest(req)) {
      return next.handle(req);
    }

    const requestId = this.generateRequestId(req);
    const startTime = Date.now();

    this.pendingRequests.set(requestId, {
      startTime,
      request: req,
    });

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.handleResponse(requestId, event);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(requestId, error);
        },
      }),
      finalize(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
        }
      })
    );
  }

  private shouldTrackRequest(req: HttpRequest<unknown>): boolean {
    const url = req.url.toLowerCase();
    const baseUrl =
      this.environmentService.appointmentSchedulingBaseUrl?.toLowerCase() || '';

    // Track /fal/search and /fal/searchByAddress
    if (url.includes('/fal/search')) {
      return true;
    }

    // Track hold and schedule on the appointment scheduling base URL
    if (baseUrl && url.startsWith(baseUrl)) {
      if (url.includes('hold') || url.includes('schedule')) {
        return req.method === 'POST';
      }
    }

    return false;
  }

  private generateRequestId(req: HttpRequest<unknown>): string {
    return `${req.method}-${req.url}-${Date.now()}-${Math.random()}`;
  }

  private extractEndpoint(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch {
      return url;
    }
  }

  private handleResponse(
    requestId: string,
    response: HttpResponse<unknown>
  ): void {
    const pendingRequest = this.pendingRequests.get(requestId);
    if (!pendingRequest) return;

    const duration = Date.now() - pendingRequest.startTime;
    const apiData: ApiMetricData = {
      endpoint: this.extractEndpoint(pendingRequest.request.url),
      method: pendingRequest.request.method,
      statusCode: response.status,
      requestData: pendingRequest.request.body,
      responseData: response.body,
      duration,
    };

    this.logMetricBasedOnEndpoint(apiData);
    this.pendingRequests.delete(requestId);
  }

  private handleError(requestId: string, error: HttpErrorResponse): void {
    const pendingRequest = this.pendingRequests.get(requestId);
    if (!pendingRequest) return;

    const duration = Date.now() - pendingRequest.startTime;
    const apiData: ApiMetricData = {
      endpoint: this.extractEndpoint(pendingRequest.request.url),
      method: pendingRequest.request.method,
      statusCode: error.status,
      requestData: pendingRequest.request.body,
      errorData: error,
      duration,
    };

    this.logMetricBasedOnEndpoint(apiData);
    this.pendingRequests.delete(requestId);
  }

  private logMetricBasedOnEndpoint(apiData: ApiMetricData): void {
    const endpoint = apiData.endpoint?.toLowerCase() || '';
    if (endpoint.includes('/fal/search')) {
      this.metricService
        .logSearchAppointmentEvent(apiData)
        .pipe(take(1))
        .subscribe();
    } else if (endpoint.includes('hold')) {
      this.metricService
        .logHoldAppointmentEvent(apiData)
        .pipe(take(1))
        .subscribe();
    } else if (endpoint.includes('schedule')) {
      this.metricService
        .logScheduleAppointmentEvent(apiData)
        .pipe(take(1))
        .subscribe();
    }
  }
}
